import { useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { MEDIA_IMAGE_CONTENT_TYPES, buildAcceptAttribute } from '@/constants/media';

const ACCEPTED_IMAGE_TYPES = buildAcceptAttribute(MEDIA_IMAGE_CONTENT_TYPES);

interface EntityImageUploadProps {
  imageUrl: string | null;
  onSelect: (file: File) => void;
  onRemove: () => void;
  isBusy: boolean;
  compact?: boolean;
}

export function EntityImageUpload({
  imageUrl,
  onSelect,
  onRemove,
  isBusy,
  compact = false,
}: EntityImageUploadProps) {
  const { t } = useTranslation('treatmentLibrary');
  const inputRef = useRef<HTMLInputElement | null>(null);

  function openPicker() {
    if (inputRef.current !== null) {
      inputRef.current.click();
    }
  }

  function handleChange(changeEvent: ChangeEvent<HTMLInputElement>) {
    const files = changeEvent.target.files;
    if (files !== null && files.length > 0) {
      const selected = files[0];
      if (selected !== undefined) {
        onSelect(selected);
      }
    }
    changeEvent.target.value = '';
  }

  const tile = (
    <button
      type="button"
      aria-label={imageUrl === null ? t('image.add') : t('image.changeLabel')}
      onClick={openPicker}
      disabled={isBusy}
      className={cn(
        'relative shrink-0 rounded-xl overflow-hidden border border-zooborder bg-zoovpalelavender flex items-center justify-center text-zooviolet transition-colors hover:border-zooprimary',
        compact ? 'w-11 h-11' : 'w-16 h-16'
      )}
    >
      {imageUrl === null ? (
        <Icon name="Image" />
      ) : (
        <img src={imageUrl} alt={t('image.alt')} className="w-full h-full object-cover" />
      )}
      {isBusy ? (
        <span className="absolute inset-0 flex items-center justify-center bg-zoowhite/70">
          <LoadingSpinner className="w-4 h-4" />
        </span>
      ) : null}
    </button>
  );

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPTED_IMAGE_TYPES}
      className="hidden"
      onChange={handleChange}
    />
  );

  if (compact) {
    return (
      <div className="relative shrink-0">
        {tile}
        {imageUrl === null ? null : (
          <button
            type="button"
            aria-label={t('image.remove')}
            onClick={onRemove}
            disabled={isBusy}
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-zoowhite border border-zooborder shadow-sm flex items-center justify-center text-zoomuted hover:text-zooerrortext"
          >
            <Icon name="X" className="text-[12px]" />
          </button>
        )}
        {hiddenInput}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      {tile}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Button variant="link" onClick={openPicker} disabled={isBusy} className="text-[13px]">
            {imageUrl === null ? t('image.add') : t('image.change')}
          </Button>
          {imageUrl === null ? null : (
            <>
              <span className="text-zooborder">·</span>
              <button
                type="button"
                onClick={onRemove}
                disabled={isBusy}
                className="text-[13px] font-semibold text-zoomuted hover:text-zooerrortext"
              >
                {t('image.remove')}
              </button>
            </>
          )}
        </div>
        <span className="text-[11px] text-zoomuted">{t('image.hint')}</span>
      </div>
      {hiddenInput}
    </div>
  );
}
